import { AnimatePresence } from 'framer-motion'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import { resolveReferenceImageDisplayUrl } from '../../api/referenceUpload'
import type { ComicCharacterCard } from '../../lib/comicCreativeBible'
import type { ComicPageReferenceCapContext } from '../../lib/comicPageReferenceCap'
import { ensureEpisodePages, getEpisodePage, getPanelsOnPage } from '../../lib/comicPages'
import { readComicPanelDragId, setComicPanelDragData } from '../../lib/comicPanelDrag'
import {
  resolveComicReferenceThumbPreview,
  shouldShowComicReferenceThumb,
} from '../../lib/comicReferenceAspectMocks'
import type { ComicEpisode, ComicPanel, ComicPanelCharacterLine } from '../../lib/comicTypes'
import { ChromePrimaryButton, ChromeToolbarButton } from '../ChromeActions'
import { ChromeMetaTag } from '../ChromeTags'
import { ChromeTooltip } from '../ChromeTooltip'
import { ThemeReveal, ThemeStagger, ThemeStaggerItem } from '../motion/ThemeMotion'
import { IconPencil, IconSwapHalfHorizontal, IconTrash } from '../nav-icons'
import { ComicPanelBatchPageMapModal } from './ComicPanelBatchPageMapModal'
import { ComicPanelEditModal } from './ComicPanelEditModal'
import { ComicPanelPageMapModal } from './ComicPanelPageMapModal'
import { comicPanelRound, comicPanelRoundSm } from './comicPanelTokens'
import {
  ComicRailAddButton,
  ComicRailMigrateButton,
  ComicRailSection,
  ComicRailSectionHeader,
} from './ComicRailSection'

function referenceThumbSrc(url: string): string {
  const trimmed = url.trim()
  if (!trimmed) return trimmed
  if (trimmed.startsWith('data:')) return trimmed
  return resolveReferenceImageDisplayUrl(trimmed)
}

function panelStatusLabel(panel: ComicPanel): string {
  switch (panel.status) {
    case 'generating':
      return '生成中'
    case 'done':
      return '已定稿'
    case 'failed':
      return '失败'
    default:
      return '草稿'
  }
}

type Props = {
  angular: boolean
  characters: ComicCharacterCard[]
  episode: ComicEpisode
  selectedPanelId: string | null
  selectedPageId: string | null
  onSelectPanel: (id: string) => void
  onAddPanel?: () => void
  onRemovePanel: (id: string) => void
  onMovePanel: (id: string, direction: 'up' | 'down') => void
  draggingPanelId?: string | null
  onDragStartPanel?: (panelId: string) => void
  onDragEndPanel?: () => void
  onPanelDropOnSlot?: (panelId: string, insertIndex: number) => void
  onMovePanelToPage: (panelId: string, targetPageId: string) => void
  onMovePanelsToPage: (panelIds: string[], targetPageId: string) => void
  onSavePanel: (
    panelId: string,
    patch: {
      script: string
      dialogue: string
      narration: string
      referenceImageUrls?: string[]
      characterIds?: string[]
      characterLines?: ComicPanelCharacterLine[]
    },
  ) => void
  panelSaveBusy?: boolean
  onSelectPanelCandidate?: (candidateId: string) => void
  referenceCap?: ComicPageReferenceCapContext | null
  canEdit?: boolean
}

export function ComicStoryboardRail({
  angular,
  characters,
  episode,
  selectedPanelId,
  selectedPageId,
  onSelectPanel,
  onAddPanel,
  onRemovePanel,
  onMovePanel,
  draggingPanelId = null,
  onDragStartPanel,
  onDragEndPanel,
  onPanelDropOnSlot,
  onMovePanelToPage,
  onMovePanelsToPage,
  onSavePanel,
  panelSaveBusy = false,
  onSelectPanelCandidate,
  referenceCap = null,
  canEdit = true,
}: Props) {
  const [dropInsertIndex, setDropInsertIndex] = useState<number | null>(null)
  const [pageMapPanelId, setPageMapPanelId] = useState<string | null>(null)
  const [batchMapOpen, setBatchMapOpen] = useState(false)
  const [editPanelId, setEditPanelId] = useState<string | null>(null)
  const normalizedEpisode = useMemo(() => ensureEpisodePages(episode), [episode])
  const episodePages = normalizedEpisode.pages
  const currentPage = getEpisodePage(normalizedEpisode, selectedPageId)
  const pagePanels = useMemo(
    () => (currentPage ? getPanelsOnPage(normalizedEpisode, currentPage) : []),
    [currentPage, normalizedEpisode],
  )
  const pageMapPanel =
    pageMapPanelId != null
      ? normalizedEpisode.panels.find((p) => p.id === pageMapPanelId) ?? null
      : null
  const editPanel =
    editPanelId != null ? normalizedEpisode.panels.find((p) => p.id === editPanelId) ?? null : null
  const canMapPages = episodePages.length > 1

  const committedPageIdRef = useRef<string | null>(null)
  const committedPanelIdsRef = useRef<string[]>([])
  const pageId = selectedPageId ?? null
  /** 须在 paint 后再提交 pageId，避免同一次切页触发的二次 setState 把 skipEnter 打成 true */
  const isPageSwitch = pageId !== committedPageIdRef.current
  const animateEnterPanelIds = new Set<string>()
  if (!isPageSwitch) {
    const prev = new Set(committedPanelIdsRef.current)
    for (const p of pagePanels) {
      if (!prev.has(p.id)) animateEnterPanelIds.add(p.id)
    }
  }

  useLayoutEffect(() => {
    committedPageIdRef.current = pageId
    committedPanelIdsRef.current = pagePanels.map((p) => p.id)
  }, [pageId, pagePanels])

  const openEdit = (panelId: string) => {
    onSelectPanel(panelId)
    setEditPanelId(panelId)
  }

  const headerActions = canEdit ? (
    <div className="flex shrink-0 items-center gap-2">
      {canMapPages ? (
        <ComicRailMigrateButton
          angular={angular}
          label="批量迁移"
          tooltip="批量迁移到其他页"
          disabled={pagePanels.length === 0}
          onClick={() => setBatchMapOpen(true)}
        />
      ) : null}
      {onAddPanel ? (
        <ComicRailAddButton angular={angular} label="本页加格" tooltip="本页加格" onClick={onAddPanel} />
      ) : null}
    </div>
  ) : null

  return (
    <ComicRailSection className="flex min-h-0 min-w-0 flex-col">
      <div className="shrink-0">
        <ComicRailSectionHeader title="分镜表" actions={headerActions} />
      </div>

      <div className="scroll-region min-h-0 max-h-[min(42vh,30rem)] overflow-y-auto overflow-x-clip pe-0.5">
        {pagePanels.length === 0 ? (
          <ThemeReveal
            className={[
              'border border-dashed border-line px-4 py-8 text-center',
              comicPanelRound(angular),
              'bg-surface-muted/35',
            ].join(' ')}
          >
            {canEdit && onAddPanel ? (
              <ChromePrimaryButton density="compact" type="button" onClick={onAddPanel}>
                添加第 1 格
              </ChromePrimaryButton>
            ) : null}
          </ThemeReveal>
        ) : (
          <ThemeStagger
            key={pageId ?? '__no-page'}
            indexStagger
            singleChild={isPageSwitch && pagePanels.length === 1}
            className="flex min-h-0 flex-col gap-2 overflow-x-clip"
          >
            <div role="list" aria-label={currentPage ? `${currentPage.title} 分镜` : '分镜列表'} className="contents">
            <AnimatePresence initial={isPageSwitch}>
              {pagePanels.map((panel, slotIdx) => {
              const selected = panel.id === selectedPanelId
              const canUp = slotIdx > 0
              const canDown = slotIdx < pagePanels.length - 1
              const isDragging = draggingPanelId === panel.id
              const dropBefore = dropInsertIndex === slotIdx
              const panelReferenceUrl = panel.referenceImageUrls?.[0]
              const referenceThumb = shouldShowComicReferenceThumb(
                panelReferenceUrl,
                slotIdx,
              )
                ? resolveComicReferenceThumbPreview(panelReferenceUrl, slotIdx, {
                    forceMock:
                      import.meta.env.VITE_COMIC_REFERENCE_MOCK_FORCE === 'true',
                  })
                : { url: '' }
              const referenceThumbUrl = referenceThumb.url
                return (
                  <ThemeStaggerItem
                    key={panel.id}
                    index={slotIdx}
                    enterMode={isPageSwitch ? 'slideFromEnd' : 'default'}
                    skipEnter={isPageSwitch ? false : !animateEnterPanelIds.has(panel.id)}
                    layoutReflow
                  >
                    <li
                      className="list-none"
                      onDragOver={
                        onPanelDropOnSlot
                          ? (e) => {
                              e.preventDefault()
                              e.dataTransfer.dropEffect = 'move'
                              setDropInsertIndex(slotIdx)
                            }
                          : undefined
                      }
                      onDragLeave={() => {
                        setDropInsertIndex((cur) => (cur === slotIdx ? null : cur))
                      }}
                      onDrop={
                        onPanelDropOnSlot
                          ? (e) => {
                              e.preventDefault()
                              setDropInsertIndex(null)
                              const id = readComicPanelDragId(e.dataTransfer)
                              if (id) onPanelDropOnSlot(id, slotIdx)
                            }
                          : undefined
                      }
                    >
                      <div
                        className={[
                          'relative border bg-panel p-2 shadow-sm transition-shadow',
                          onPanelDropOnSlot || canMapPages || onDragStartPanel ? 'ps-9' : '',
                          selected
                            ? 'border-accent shadow-card ring-1 ring-inset ring-accent/35'
                            : 'border-line',
                          isDragging ? 'opacity-45' : '',
                          dropBefore ? 'ring-2 ring-inset ring-accent/60' : '',
                          comicPanelRoundSm(angular),
                        ].join(' ')}
                      >
                        <button
                          type="button"
                          onClick={() => onSelectPanel(panel.id)}
                          className={[
                            'absolute inset-0 z-0 rounded-[inherit]',
                            'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50',
                          ].join(' ')}
                          aria-label={`选择分镜 格 ${slotIdx + 1}${
                            panel.script.trim() ? `：${panel.script.trim().slice(0, 48)}` : ''
                          }`}
                        />
                        <div
                          className={[
                            'absolute left-1.5 top-2 z-[2] flex w-6 flex-col items-center gap-1',
                            'pointer-events-none',
                          ].join(' ')}
                        >
                          {onDragStartPanel ? (
                            <button
                              type="button"
                              draggable
                              className={[
                                'pointer-events-auto flex h-7 w-6 cursor-grab items-center justify-center text-ink-soft active:cursor-grabbing',
                                'touch-none select-none hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
                              ].join(' ')}
                              aria-label={`拖拽分镜 格 ${slotIdx + 1}`}
                              onClick={(e) => e.stopPropagation()}
                              onDragStart={(e) => {
                                setComicPanelDragData(e.dataTransfer, panel.id)
                                onDragStartPanel(panel.id)
                              }}
                              onDragEnd={() => onDragEndPanel?.()}
                            >
                              <span aria-hidden className="text-sm leading-none">
                                ⠿
                              </span>
                            </button>
                          ) : null}
                          {canEdit && canMapPages ? (
                            <ChromeTooltip content="切换页面" angular={angular} side="top" align="center">
                              <button
                                type="button"
                                className={[
                                  'pointer-events-auto flex h-7 w-6 items-center justify-center text-ink-soft transition hover:text-ink',
                                  'touch-none select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
                                ].join(' ')}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setPageMapPanelId(panel.id)
                                }}
                                aria-label={`将分镜 ${panel.order} 移动到其他页`}
                              >
                                <IconSwapHalfHorizontal className="h-4 w-4" aria-hidden />
                              </button>
                            </ChromeTooltip>
                          ) : null}
                          {canEdit ? (
                            <ChromeTooltip content="删除分镜" angular={angular} side="top" align="center">
                              <button
                                type="button"
                                className={[
                                  'pointer-events-auto flex h-7 w-6 items-center justify-center text-red-600 transition hover:text-red-700',
                                  'touch-none select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
                                ].join(' ')}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onRemovePanel(panel.id)
                                }}
                                aria-label={`删除分镜 ${panel.order}`}
                              >
                                <IconTrash className="h-4 w-4" aria-hidden />
                              </button>
                            </ChromeTooltip>
                          ) : null}
                        </div>
                        <div className="relative z-[1] pointer-events-none min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold tabular-nums text-ink-soft">
                              格 {slotIdx + 1}
                            </span>
                            <ChromeMetaTag angular={angular}>{panelStatusLabel(panel)}</ChromeMetaTag>
                            {canEdit ? (
                              <ChromeTooltip
                                content="编辑"
                                angular={angular}
                                side="top"
                                align="center"
                              >
                                <ChromeToolbarButton
                                  type="button"
                                  className="pointer-events-auto ms-auto h-7 w-7 !gap-0 !p-0"
                                  aria-label={`编辑分镜 格 ${slotIdx + 1}`}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    openEdit(panel.id)
                                  }}
                                >
                                  <IconPencil className="h-3.5 w-3.5" aria-hidden />
                                </ChromeToolbarButton>
                              </ChromeTooltip>
                            ) : null}
                          </div>
                          <div className="mt-1 flex min-w-0 items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="line-clamp-2 text-sm text-ink">
                                {panel.script.trim() || '（未填写画面描述）'}
                              </p>
                              {panel.dialogue.trim() ? (
                                <p className="mt-0.5 line-clamp-1 text-xs text-ink-soft">
                                  「{panel.dialogue.trim()}」
                                </p>
                              ) : null}
                              {(panel.narration ?? '').trim() ? (
                                <p className="mt-0.5 line-clamp-1 text-xs text-ink-soft/90">
                                  {panel.narration?.trim()}
                                </p>
                              ) : null}
                              {canEdit ? (
                              <div className="relative z-[2] mt-2 flex w-fit max-w-full items-center gap-1 pointer-events-none">
                                <ChromeToolbarButton
                                  type="button"
                                  className="pointer-events-auto h-7 min-h-0 px-1.5 text-[11px] font-semibold"
                                  disabled={!canUp}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onMovePanel(panel.id, 'up')
                                  }}
                                  aria-label={`上移分镜 ${panel.order}`}
                                >
                                  ↑
                                </ChromeToolbarButton>
                                <ChromeToolbarButton
                                  type="button"
                                  className="pointer-events-auto h-7 min-h-0 px-1.5 text-[11px] font-semibold"
                                  disabled={!canDown}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onMovePanel(panel.id, 'down')
                                  }}
                                  aria-label={`下移分镜 ${panel.order}`}
                                >
                                  ↓
                                </ChromeToolbarButton>
                              </div>
                              ) : null}
                            </div>
                            {referenceThumbUrl ? (
                              <div
                                className={[
                                  'relative aspect-square h-[4.25rem] w-[4.25rem] shrink-0 overflow-hidden border border-line bg-surface-muted/35',
                                  angular ? 'rounded-none' : 'rounded-md',
                                ].join(' ')}
                              >
                                <img
                                  src={referenceThumbSrc(referenceThumbUrl)}
                                  alt=""
                                  className="h-full w-full object-cover object-center"
                                  draggable={false}
                                  loading="lazy"
                                  decoding="async"
                                />
                                {import.meta.env.DEV && referenceThumb.mockRatio ? (
                                  <span
                                    className="pointer-events-none absolute bottom-0 right-0 bg-ink/70 px-0.5 text-[9px] font-medium leading-tight text-surface"
                                    aria-hidden
                                  >
                                    {referenceThumb.mockRatio}
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </li>
                  </ThemeStaggerItem>
                )
              })}
            </AnimatePresence>
            </div>
          </ThemeStagger>
        )}
      </div>

      <ComicPanelEditModal
        open={editPanelId != null}
        angular={angular}
        characters={characters}
        page={currentPage}
        panel={editPanel}
        saving={panelSaveBusy}
        referenceCap={referenceCap}
        onClose={() => setEditPanelId(null)}
        onSave={(patch) => {
          if (!editPanelId) return
          onSavePanel(editPanelId, patch)
          setEditPanelId(null)
        }}
        onSelectPanelCandidate={
          onSelectPanelCandidate
            ? (id) => {
                onSelectPanelCandidate(id)
              }
            : undefined
        }
      />

      <ComicPanelBatchPageMapModal
        open={batchMapOpen}
        angular={angular}
        sourcePage={currentPage}
        panels={pagePanels}
        pages={episodePages}
        onClose={() => setBatchMapOpen(false)}
        onMove={(panelIds, targetPageId) => onMovePanelsToPage(panelIds, targetPageId)}
      />
      <ComicPanelPageMapModal
        open={pageMapPanelId != null}
        angular={angular}
        panel={pageMapPanel}
        pages={episodePages}
        currentPageId={selectedPageId}
        onClose={() => setPageMapPanelId(null)}
        onMove={(targetPageId) => {
          if (pageMapPanelId) onMovePanelToPage(pageMapPanelId, targetPageId)
        }}
      />
    </ComicRailSection>
  )
}
